import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { ArrowRight, Download, FileText } from 'lucide-react'
import { Link } from 'react-router-dom'
import { FinancialReport } from '../../../repositories/ReportRepository'

interface OwnerReportTableProps {
  reportData: FinancialReport[]
  loading: boolean
  onExportPDF: () => void
  onExportOwnerPDF: (ownerId: string) => void
}

export function OwnerReportTable({
  reportData,
  loading,
  onExportPDF,
  onExportOwnerPDF,
}: OwnerReportTableProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const getOccupancyRateBagdeColor = (occupancyRate: number) => {
    if (occupancyRate >= 70) {
      return 'green'
    } else if (occupancyRate >= 40) {
      return 'yellow'
    } else {
      return 'red'
    }
  }

  return (
    <Card>
      <CardBody>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">Relatório de Proprietários</Heading>
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            leftIcon={<Icon as={Download} boxSize={4} />}
            onClick={onExportPDF}
          >
            Exportar Relatório Geral
          </Button>
        </Flex>

        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Proprietário</Th>
                <Th isNumeric>Propriedades</Th>
                <Th isNumeric>Receita Total</Th>
                <Th isNumeric>Repasses</Th>
                <Th isNumeric>Tx. Limpeza</Th>
                <Th isNumeric>Manutenções</Th>
                <Th isNumeric>Lucro</Th>
                <Th isNumeric>Diária Média</Th>
                <Th isNumeric>Tx. de Ocup.</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {reportData.map((item) => (
                <Tr key={item.owner_id} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {item.owner_name || 'N/A'}
                      </Text>
                    </VStack>
                  </Td>
                  <Td isNumeric>{item.properties_count}</Td>
                  <Td isNumeric color={'green.500'} fontWeight={'bold'}>
                    {formatCurrency(item.total_revenue)}
                  </Td>
                  <Td isNumeric color={'red.500'} fontWeight={'bold'}>
                    {formatCurrency(
                      item.total_fowarding_fee + item.company_commission
                    )}
                  </Td>
                  <Td isNumeric color={'red.500'} fontWeight={'bold'}>
                    {formatCurrency(item.total_cleaning_fee)}
                  </Td>
                  <Td isNumeric color={'red.500'} fontWeight={'bold'}>
                    {formatCurrency(item.total_maintenance_fee)}
                  </Td>
                  <Td isNumeric color={'blue.500'} fontWeight={'bold'}>
                    {formatCurrency(
                      item.total_revenue -
                        (item.total_fowarding_fee +
                          item.company_commission +
                          item.total_cleaning_fee +
                          item.total_maintenance_fee)
                    )}
                  </Td>
                  <Td isNumeric color={'green.500'} fontWeight={'bold'}>
                    {formatCurrency(item.adr)}
                  </Td>
                  <Td isNumeric>
                    <Badge
                      px={2}
                      py={1}
                      colorScheme={getOccupancyRateBagdeColor(
                        item.occupancy_rate
                      )}
                      rounded="md"
                    >
                      {item.occupancy_rate.toFixed(2).replace('.', ',')}%
                    </Badge>
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="red"
                      leftIcon={<Icon as={Download} boxSize={4} />}
                      onClick={() => onExportOwnerPDF(item.owner_id)}
                    >
                      PDF
                    </Button>
                    <Button
                      as={Link}
                      to={`/reports/owner/${item.owner_id}`}
                      size="xs"
                      variant="ghost"
                      rightIcon={<Icon as={ArrowRight} boxSize={4} />}
                      ml={2}
                    >
                      Detalhes
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {reportData.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <Icon as={FileText} boxSize={12} color="gray.400" mb={4} />
            <Text color="gray.500" fontSize="lg">
              Nenhum dado encontrado para o período selecionado
            </Text>
            <Text color="gray.400" fontSize="sm">
              Ajuste os filtros ou verifique se existem reservas no período
            </Text>
          </Box>
        )}
      </CardBody>
    </Card>
  )
}
